/*
 * Install sheet of the build page (SPEC decision 28): pure model, no React. Which app file to offer, when the Install
 * button shows, the expiry line of the 15-minute link, the steps on the phone and the title.
 */
import type { Artifact, Build } from '@/api/schemas';
import type { ArtifactInstall } from '@/api/endpointsInstall';

/** Installable kinds first (APK, then IPA), then the files whose sheet only explains why they do not install. */
const RANK: Record<string, number> = { apk: 0, ipa: 1, aab: 2, app_zip: 3, app_tar_gz: 3 };

/** The build's app file to offer: the best ranked binary still stored, else the best ranked one; null without any. */
export function installCandidate(artifacts: readonly Artifact[] | undefined): Artifact | null {
  const binaries = (artifacts ?? []).filter((a) => a.kind in RANK);
  binaries.sort((a, b) => RANK[a.kind]! - RANK[b.kind]! || Number(Boolean(a.deletedAt)) - Number(Boolean(b.deletedAt)));
  return binaries[0] ?? null;
}

/** Install shows on a build that succeeded and produced an app file; the sheet explains when that file cannot install. */
export const showInstall = (b: Pick<Build, 'status'>, artifacts: readonly Artifact[] | undefined): boolean => b.status === 'succeeded' && installCandidate(artifacts) !== null;

/** Whole minutes left (rounded up), never negative. */
export function minutesLeft(expiresAt: string | null | undefined, now: number): number {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 60_000));
}

export function expiryText(expiresAt: string | null | undefined, now: number): string {
  const left = minutesLeft(expiresAt, now);
  if (left <= 0) return 'This link expired. Make a new one.';
  return left === 1 ? 'The link works for 1 more minute.' : `The link works for ${left} more minutes.`;
}

/** What the person does on the phone. */
export function installSteps(info: Pick<ArtifactInstall, 'platform'>): string[] {
  return info.platform === 'ios'
    ? ['Open the camera on the iPhone or iPad and scan the code.', 'Open the page in Safari and tap Install.', 'The app appears on the home screen when it is installed.']
    : ['Open the camera on the Android phone and scan the code.', 'Tap Install on the page and open the downloaded file.', 'If Android asks, allow the browser to install apps.'];
}

/** "QA App 1.4.0 (42)". */
export function installTitle(info: Pick<ArtifactInstall, 'app'>): string {
  const version = info.app.versionName ? ` ${info.app.versionName}` : '';
  const build = info.app.buildNumber !== null && info.app.buildNumber !== undefined ? ` (${info.app.buildNumber})` : '';
  return `${info.app.name}${version}${build}`;
}

/** Reasons where registering devices or building ad hoc is the way forward (the sheet links to Devices). */
export const pointsToDevices = (info: Pick<ArtifactInstall, 'platform' | 'reason' | 'requiresRegisteredDevice'>): boolean =>
  info.platform === 'ios' && (info.requiresRegisteredDevice || info.reason === 'store_signed' || info.reason === 'unknown_signing');
